import useOptimisticStatus from 'Components/hooks/use-optimistic-status'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  useRef,
} from 'react'
import { focus } from 'lib/ugc-interact'
import { unlike } from 'New/groups/services/group'
import useLoginCheckCallback from 'Components/hooks/use-login-check-callback'

const followedMap: Record<
  string,
  {
    length: number
    map: Record<
      string,
      {
        setCurStatus: Dispatch<SetStateAction<boolean>>
        setCurCount: Dispatch<SetStateAction<number>>
      }
    >
  }
> = {}

let id = 0

// 关注/取消关注
function useToggleFollow({
  userId,
  followed = false,
  followCount = 0,
}: {
  userId?: string | number
  followed?: boolean
  followCount?: number
}) {
  const [curStatus, setCurStatus] = useState(followed)
  const [curCount, setCurCount] = useState(followCount)
  useEffect(() => {
    setCurStatus(followed)
  }, [followed])
  useEffect(() => {
    setCurCount(followCount)
  }, [followCount])

  const [uid] = useState(() => {
    return ++id
  })

  useEffect(() => {
    if (!userId) return
    if (!followedMap[userId]) {
      followedMap[userId] = {
        map: {},
        length: 0,
      }
    }
    if (!followedMap[userId].map[uid]) {
      followedMap[userId].map[uid] = { setCurStatus, setCurCount }
      followedMap[userId].length++
    }
    return () => {
      delete followedMap[userId].map[uid]
      followedMap[userId].length--
      if (!followedMap[userId].length) {
        delete followedMap[userId]
      }
    }
  }, [userId])

  return {
    update: useOptimisticStatus({
      execute: useLoginCheckCallback(
        useCallback(
          ([curFollow]) => {
            return (curFollow ? focus : unlike)({
              user_id: userId!,
              to_user_id: userId!,
            }).then(() => {
              return true
            })
          },
          [userId]
        ),
        useMemo(
          () => ({
            enter_method: 'follow',
          }),
          []
        )
      ),
      begin: useCallback<
        () => {
          prev: [boolean, number]
          next: [boolean, number]
        }
      >(() => {
        setCurStatus(!curStatus)
        const count = Math.max(0, curStatus ? curCount - 1 : curCount + 1)
        setCurCount(count)
        Object.values(followedMap[userId!]?.map || {}).forEach(
          ({ setCurStatus, setCurCount }) => {
            setCurStatus(!curStatus)
            setCurCount(count)
          }
        )
        return {
          prev: [curStatus, curCount],
          next: [!curStatus, count],
        }
      }, [userId, curStatus, curCount]),
      rollback: useCallback(
        ([curStatus, curCount]: [boolean, number]) => {
          setCurStatus(curStatus)
          setCurCount(curCount)
          Object.values(followedMap[userId!]?.map || {}).forEach(
            ({ setCurStatus, setCurCount }) => {
              setCurStatus(curStatus)
              setCurCount(curCount)
            }
          )
        },
        [userId]
      ),
    }),
    curStatus,
    curCount,
  }
}

export default useToggleFollow
